import type { Message } from "@/lib/core/crm/types";

/**
 * Minimal shape of a `communications` row needed to render the email / message
 * detail page. Matches the fields selected by the GetCommunications GraphQL
 * query (pg_graphql, RLS-scoped) — see src/operations/communications.graphql.
 */
export interface CommunicationRecordForMessage {
  id: string;
  title?: string | null;
  body?: string | null;
  body_preview?: string | null;
  contact_name?: string | null;
  company_name?: string | null;
  occurred_at?: string | null;
  direction?: string | null;
  external_id?: string | null;
}

/**
 * Map an own-infra `communications` record onto the canonical Message shape the
 * CRM detail pages (UniversalDetailPage + Email/Message DetailConfig) consume.
 *
 * This is the replacement for the retired Algolia `emails_search` / `hub` detail
 * reads: the record comes from Postgres by id (RLS), and `resolvedBody` is the
 * full body already hydrated from S3-or-inline via GET /api/communications/[id]/body.
 *
 * Notes on the mapping:
 * - `title` → `subject`; `occurred_at` → both sent/received timestamps.
 * - `provider` is a valid ProviderKey ("communication") since these records are
 *   synced CRM communications, not a live Gmail/Outlook fetch.
 * - `sender.email` is intentionally empty: the communications row keeps the
 *   counterparty as `contact_name`, not a structured address. Detail-page tabs
 *   render `sender.name` and fall back gracefully when the email is absent.
 */
export function mapCommunicationToMessage(
  record: CommunicationRecordForMessage,
  resolvedBody: string | null
): Message {
  const occurredAt = record.occurred_at ?? new Date().toISOString();
  const body = resolvedBody ?? record.body ?? "";

  return {
    id: record.id,
    subject: record.title ?? "(No Subject)",
    body,
    bodyPreview: record.body_preview ?? "",
    sender: {
      name: record.contact_name ?? record.company_name ?? "",
      email: "",
    },
    recipients: [],
    receivedDateTime: occurredAt,
    sentDateTime: occurredAt,
    isRead: true,
    hasAttachments: false,
    importance: "normal",
    conversationId: record.external_id ?? "",
    provider: "communication",
    providerMessageId: record.external_id ?? record.id,
    inboxId: "",
  };
}
